import React, { useState } from "react";
import { styled } from "styled-components";

import type { Field, MultiSelectField } from "./types";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { useChatClass, useTheme } from "../../theme";
import { DateRangePicker } from "./fields/DateRange";
import { FieldGroup, Label, HelperText } from "./shared";
import { MultiSelect } from "./fields/MultiSelect";
import { TextField } from "./fields/TextField";
import { TextareaField } from "./fields/TextareaField";

const CheckboxRow = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.space["2"],
}));

const CheckboxInput = styled.input(({ theme }) => ({
  marginTop: 3,
  width: 16,
  height: 16,
  accentColor: theme.colors.primary,
}));

const RequiredAsterisk = styled.span(({ theme }) => ({
  color: theme.colors.warn,
}));

const HelpLinkAnchor = styled.a(({ theme }) => ({
  color: theme.colors.primaryDark,
  textDecoration: "underline",
  fontSize: "14px",
}));

const Overlay = styled.div(() => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.35)",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-end",
  zIndex: 2,
}));

const Panel = styled.div(({ theme }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: theme.colors.white,
  borderTopLeftRadius: theme.radii["2"],
  borderTopRightRadius: theme.radii["2"],
  boxShadow: theme.shadows.surfaceZ1Light,
  display: "flex",
  flexDirection: "column",
}));

const Header = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "20px 20px",
  borderBottom: `1px solid rgba(0,0,0,0.1)`,
}));
const HeaderLeft = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 12,
});
const HeaderRight = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const Title = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeights["2"],
  fontSize: theme.fontSizes["3"],
  lineHeight: theme.lineHeights["3"],
}));

const Instructions = styled.div(({ theme }) => ({
  borderLeft: `4px solid ${theme.colors.primary}`,
  padding: theme.space["3"],
  background: theme.colors.primaryLightest,
  marginBottom: theme.space["4"],
}));

const Body = styled.div(() => ({
  flex: 1,
  overflowY: "auto",
  padding: "20px",
}));

const Content = styled.div(() => ({
  width: "100%",
  maxWidth: 560,
  margin: "0 auto",
}));

const Footer = styled.div(() => ({
  display: "flex",
  gap: "12px",
  padding: "20px",
  borderTop: `1px solid rgba(0,0,0,0.1)`,
  backgroundColor: "#fff",
}));

export interface FormOverlayProps {
  /** Shown at very top of form */
  title?: string;
  /** Subtle text below the title */
  subtitle?: string;
  /** Optional instructions to complete form.
   * Displayed below the title and above the fields.*/
  instructions?: string;
  onClose: () => void;
  onSubmit?: () => void;
  /** Optional label to override default "Submit" */
  submitLabel?: string;
  busy?: boolean;
  children?: React.ReactNode;
  /** Optional custom footer; if provided, replaces default submit footer */
  footer?: React.ReactNode;
  /** Optional content on the left side of header (e.g., back button) */
  headerLeft?: React.ReactNode;
  /** If false, hide the close button and disable Escape-to-close */
  dismissible?: boolean;
}

/** Full-screen form overlay rendered inside the chat window. */
export const FormOverlay: React.FC<FormOverlayProps> = ({
  title,
  subtitle,
  instructions,
  onClose,
  onSubmit,
  submitLabel = "Submit",
  busy,
  children,
  footer,
  headerLeft,
  dismissible = true,
}) => {
  const theme = useTheme();
  const chatClass = useChatClass();

  // Handle Escape key to close form
  React.useEffect(() => {
    if (!dismissible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, dismissible]);

  return (
    <Overlay
      className={chatClass("form-overlay")}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "form-title" : undefined}
      aria-describedby={subtitle ? "form-subtitle" : undefined}
    >
      <Panel>
        <Header>
          <HeaderLeft className={chatClass("form-header-left")}>
            {headerLeft}
            <div>
              {title && <Title id="form-title">{title}</Title>}
              {subtitle && (
                <HelperText id="form-subtitle">{subtitle}</HelperText>
              )}
            </div>
          </HeaderLeft>
          <HeaderRight>
            {dismissible ? (
              <Button
                variant="subtle"
                onClick={onClose}
                aria-label="Close form"
              >
                <Icon
                  size={20}
                  name="close"
                  color={theme.colors.black}
                  aria-hidden="true"
                />
              </Button>
            ) : null}
          </HeaderRight>
        </Header>
        <Body>
          {instructions && (
            <Instructions role="note" aria-label="Form instructions">
              {instructions}
            </Instructions>
          )}
          <Content role="form">{children}</Content>
        </Body>
        <Footer>
          {footer ?? (
            <Button
              width="100%"
              variant="info"
              onClick={onSubmit}
              disabled={busy}
              aria-label={busy ? "Submitting form" : submitLabel}
              aria-busy={busy}
            >
              {busy ? "Submitting..." : submitLabel}
            </Button>
          )}
        </Footer>
      </Panel>
    </Overlay>
  );
};

// Basic field renderer (single-step). Multi-step can wrap this in the future.
export const Fields: React.FC<{
  fields: Field[];
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
}> = ({ fields, values, onChange }) => {
  const chatClass = useChatClass();
  return (
    <div className={chatClass("form-fields")}>
      {fields.map((f) => {
        const value =
          values[f.id] !== undefined
            ? values[f.id]
            : f.type === "checkbox"
              ? f.defaultValue ?? false
              : f.defaultValue ?? "";

        if (
          f.type === "text" ||
          f.type === "email" ||
          f.type === "number" ||
          f.type === "tel"
        ) {
          return (
            <TextField
              key={f.id}
              field={f as any}
              value={value}
              onChange={(v) => onChange({ ...values, [f.id]: v })}
            />
          );
        }
        if (f.type === "checkbox") {
          const checked = Boolean(value);
          return (
            <FieldGroup key={f.id}>
              <CheckboxRow>
                <CheckboxInput
                  type="checkbox"
                  id={f.id}
                  checked={checked}
                  onChange={(e) =>
                    onChange({ ...values, [f.id]: e.currentTarget.checked })
                  }
                  aria-required={f.required}
                  aria-describedby={
                    f.helpText || f.helpLinks?.length
                      ? `${f.id}-help`
                      : undefined
                  }
                />
                <div>
                  <Label htmlFor={f.id}>
                    {f.label}
                    {f.required && (
                      <RequiredAsterisk aria-label="required">
                        {" "}
                        *
                      </RequiredAsterisk>
                    )}
                  </Label>
                  {f.helpText || f.helpLinks?.length ? (
                    <HelperText id={`${f.id}-help`}>
                      {f.helpText}
                      {f.helpLinks?.map((link) => (
                        <React.Fragment key={link.url}>
                          {" "}
                          <HelpLinkAnchor
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {link.label}
                          </HelpLinkAnchor>
                        </React.Fragment>
                      ))}
                    </HelperText>
                  ) : null}
                </div>
              </CheckboxRow>
            </FieldGroup>
          );
        }
        if (f.type === "textarea") {
          return (
            <TextareaField
              key={f.id}
              field={f as any}
              value={value}
              onChange={(v) => onChange({ ...values, [f.id]: v })}
            />
          );
        }
        if (f.type === "dateRange") {
          return (
            <DateRangePicker
              key={f.id}
              field={f as any}
              value={value || undefined}
              onChange={(val) => onChange({ ...values, [f.id]: val })}
            />
          );
        }
        if (f.type === "multiSelect") {
          return (
            <MultiSelect
              key={f.id}
              field={f as MultiSelectField}
              selected={
                Array.isArray(value) ? (value as Array<string | number>) : []
              }
              onChange={(ids) => onChange({ ...values, [f.id]: ids })}
            />
          );
        }
        // Fallback
        return (
          <FieldGroup key={f.id}>
            <Label>{f.label}</Label>
            <HelperText>
              Unsupported field type "{f.type}" (read-only)
            </HelperText>
          </FieldGroup>
        );
      })}
    </div>
  );
};

export interface MultiStepFormProps {
  steps: Array<{
    id: string;
    title?: string;
    subtitle?: string;
    instructions?: string;
    /** Default "Next" or "Submit" on last  */
    buttonLabel?: string;
    fields: Field[];
  }>;
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  onSubmit: () => void;
  onClose: () => void;
  busy?: boolean;
  /** If false, hide the close button and disable Escape-to-close */
  dismissible?: boolean;
}

/** Pragmatic email shape check (local-part@domain.tld). */
const EMAIL_VALUE_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isEmailValueValid = (value: unknown): boolean => {
  const trimmed = String(value ?? "").trim();
  const valid = Boolean(trimmed) && EMAIL_VALUE_PATTERN.test(trimmed);
  return valid;
};

/** Helper to check if a field value is filled/valid */
const isFieldValid = (field: Field, value: any): boolean => {
  if (!field.required) {
    let valid = true;
    if (field.type === "email") {
      const trimmed = String(value ?? "").trim();
      valid = !trimmed || isEmailValueValid(trimmed);
    }
    return valid;
  }

  if (field.type === "checkbox") {
    return value === true;
  }

  // For multiSelect, check if array has items
  if (field.type === "multiSelect") {
    return Array.isArray(value) && value.length > 0;
  }

  // For dateRange, check if both start date is present
  if (field.type === "dateRange") {
    return value && value.start;
  }

  // For tel, check if it has at least 8 digits
  if (field.type === "tel") {
    if (!value) return false;
    const digits = String(value).replace(/\D/g, "");
    return digits.length >= 8;
  }

  // For email, require a plausible address when the field is required (or optional
  // with a non-empty value — handled above).
  if (field.type === "email") {
    return isEmailValueValid(value);
  }

  // For textarea and text/number, check if not empty
  if (
    field.type === "textarea" ||
    field.type === "text" ||
    field.type === "number"
  ) {
    return Boolean(value && String(value).trim());
  }

  // Default: check if not empty
  return Boolean(value);
};

/** Multi-step form composed within FormOverlay. It handles Back/Next and final Submit. */
export const MultiStepForm: React.FC<MultiStepFormProps> = ({
  steps,
  values,
  onChange,
  onSubmit,
  onClose,
  busy,
  dismissible,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const totalSteps = steps.length;
  const step = steps[stepIndex];

  const canGoBack = stepIndex > 0;
  const isLast = stepIndex === totalSteps - 1;

  // Check if all required fields on current step are filled
  const isCurrentStepValid = step.fields.every((field) => {
    const value =
      values[field.id] !== undefined ? values[field.id] : field.defaultValue;
    return isFieldValid(field, value);
  });

  return (
    <FormOverlay
      title={step.title}
      subtitle={step.subtitle}
      instructions={step.instructions}
      onClose={onClose}
      onSubmit={onSubmit}
      busy={busy}
      dismissible={dismissible}
      headerLeft={
        canGoBack ? (
          <Button
            variant="subtle"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            aria-label="Go back to previous step"
          >
            <Icon name="large-arrow-left" size={20} aria-hidden="true" />
          </Button>
        ) : undefined
      }
      footer={
        isLast ? (
          <Button
            width="100%"
            variant="info"
            onClick={onSubmit}
            disabled={busy || !isCurrentStepValid}
          >
            {busy ? "Submitting..." : step.buttonLabel || "Submit"}
          </Button>
        ) : (
          <Button
            variant="info"
            onClick={() => setStepIndex((i) => Math.min(totalSteps - 1, i + 1))}
            width="100%"
            disabled={!isCurrentStepValid}
          >
            {step.buttonLabel || "Next"}
          </Button>
        )
      }
    >
      <Fields fields={step.fields} values={values} onChange={onChange} />
    </FormOverlay>
  );
};

export interface SingleStepFormProps {
  /** Form title */
  title?: string;
  /** Form subtitle */
  subtitle?: string;
  /** Form instructions */
  instructions?: string;
  /** Submit button label */
  buttonLabel?: string;
  /** Form fields */
  fields: Field[];
  /** Form values */
  values: Record<string, any>;
  /** Callback when values change */
  onChange: (next: Record<string, any>) => void;
  /** Callback when form is submitted */
  onSubmit: () => void;
  /** Callback when form is closed */
  onClose: () => void;
  /** Whether form is submitting */
  busy?: boolean;
  /** If false, hide the close button and disable Escape-to-close */
  dismissible?: boolean;
}

/** Single-step form with validation. Wraps FormOverlay with Fields and submit button. */
export const SingleStepForm: React.FC<SingleStepFormProps> = ({
  title,
  subtitle,
  instructions,
  buttonLabel = "Submit",
  fields,
  values,
  onChange,
  onSubmit,
  onClose,
  busy,
  dismissible,
}) => {
  // Check if all required fields are filled and valid
  const isFormValid = fields.every((field) => {
    const value =
      values[field.id] !== undefined ? values[field.id] : field.defaultValue;
    return isFieldValid(field, value);
  });

  return (
    <FormOverlay
      title={title}
      subtitle={subtitle}
      instructions={instructions}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={buttonLabel}
      busy={busy}
      dismissible={dismissible}
      footer={
        <Button
          width="100%"
          variant="info"
          onClick={onSubmit}
          disabled={busy || !isFormValid}
          aria-label={busy ? "Submitting form" : buttonLabel}
          aria-busy={busy}
        >
          {busy ? "Submitting..." : buttonLabel}
        </Button>
      }
    >
      <Fields fields={fields} values={values} onChange={onChange} />
    </FormOverlay>
  );
};
